// Document model. A single serializable object describes everything needed to
// reproduce a design: canvas, boundary, shape, layout mode + seed, and the
// gradient controller sets. Every layout mode consumes the same document.

export interface Vec2 {
  x: number
  y: number
}

export type FalloffKind = 'linear' | 'smooth' | 'sharp' | 'soft'
export type ControllerKind = 'point' | 'line' | 'curve' | 'polyline'

// Which side of a line-like controller (relative to the direction of travel,
// in the document's y-down frame) receives influence. 'off' means both sides.
export type SingleSide = 'off' | 'left' | 'right'

export interface Controller {
  id: string
  kind: ControllerKind
  a: Vec2
  b: Vec2 // only meaningful for kind 'line'
  nodes: Vec2[] // curve and polyline nodes; empty for point and line
  value: number
  radius: number // influence radius in mm
  falloff: FalloffKind
  hardEdge: boolean
  enabled: boolean
  singleSide: SingleSide // ignored for kind 'point'
  infinite: boolean // extend first/last segments to infinity (line-like kinds)
  syncGroup: string | null // controllers sharing a group share geometry + radius
}

export type ChannelId = 'size' | 'spacing' | 'angle' | 'shape'

// One place to enumerate the channels; UI order follows this.
export const CHANNEL_IDS = ['size', 'spacing', 'angle', 'shape'] as const

export interface ImageRect {
  x: number
  y: number
  w: number
  h: number
}

// Image as controller: luminance drives the channel value inside the rect
// with weight 1, blending with other controllers by the usual rule.
export interface ImageController {
  enabled: boolean
  dataUri: string
  rect: ImageRect // mm
  invert: boolean
  contrast: number // -1 flat .. 0 linear .. +1 threshold
  valueLo: number // channel value where the image is black
  valueHi: number // channel value where the image is white
}

export interface ControllerSet {
  base: number
  controllers: Controller[]
  image?: ImageController | null
}

// Decoded luminance grid (0..1), built outside the pipeline and passed in,
// so generation stays deterministic and testable without DOM image decode.
export interface LumaGrid {
  w: number
  h: number
  data: Float32Array
}

export type AssetMap = Record<string, LumaGrid>

export interface BackgroundImage {
  dataUri: string
  opacity: number // 0..1
  width: number // displayed width in mm; height follows the aspect ratio
  x: number // top-left, mm
  y: number
  visible: boolean
}

export type BoundaryKind = 'rect' | 'circle' | 'polygon'

export interface Boundary {
  kind: BoundaryKind
  // rect
  width: number
  height: number
  cornerRadius: number // rect only: rounded panel corners, mm
  // circle
  radius: number
  // polygon: user-drawn outline (may be concave, never self-intersecting;
  // sanitized on input)
  nodes: Vec2[]
}

export type ShapeKind = 'circle' | 'slot' | 'polygon' | 'star' | 'custom'

export interface ShapeStyle {
  kind: ShapeKind
  slotRatio: number // slot width / length, 0..1
  sides: number // polygon
  starPoints: number
  starInner: number // inner radius ratio, 0..1
}

// Custom shape editor: layers of primitives combined in order by boolean
// role. Design units are arbitrary; the compiled result is normalized so its
// longest dimension is 1, then scaled by the size field at placement.
export type LayerKind = 'circle' | 'rect' | 'slot' | 'polygon' | 'star' | 'outline'
export type LayerRole = 'union' | 'subtract'

export interface ShapeLayer {
  id: string
  kind: LayerKind
  role: LayerRole
  x: number // center offset, design units
  y: number
  w: number // bounding width, design units
  h: number // bounding height
  rotation: number // degrees
  cornerRadius: number // fillet radius, design units (rect/polygon/star/outline)
  sides: number
  starPoints: number
  starInner: number
  mirrorH: boolean
  mirrorV: boolean
  outline: Vec2[] // imported outline (kind 'outline'), any units; fit to w x h
}

export interface CustomShape {
  layers: ShapeLayer[]
}

export function defaultShapeLayer(kind: LayerKind): ShapeLayer {
  return {
    id: 'l' + Math.random().toString(36).slice(2, 10),
    kind,
    role: 'union',
    x: 0,
    y: 0,
    w: 10,
    h: 10,
    rotation: 0,
    cornerRadius: 0,
    sides: 6,
    starPoints: 5,
    starInner: 0.5,
    mirrorH: false,
    mirrorV: false,
    outline: [],
  }
}

export type ModeId =
  | 'grid'
  | 'scatter'
  | 'spiral'
  | 'concentric'
  | 'fibonacci'
  | 'crosshatch'
  | 'voronoi'
  | 'path'
  | 'flowlines'

// A user-drawn path for path mode. Shapes step along it by arc length with
// the spacing field; multiple paths per document.
export interface PathDef {
  id: string
  kind: 'curve' | 'polyline'
  nodes: Vec2[]
}

export interface LayoutConfig {
  mode: ModeId
  seed: number
  // crossRowSpin: cumulative rotation per row; spinBy: per-instance increment;
  // angle rotates the whole lattice (all degrees).
  grid: { pattern: 'rect' | 'hex'; crossRowSpin: number; spinBy: number; angle: number }
  crosshatch: { angleA: number; angleB: number }
  voronoi: { cornerRadius: number } // mm, arc insertion at cell corners
  flow: { cap: 'round' | 'flat' } // stroke end caps for flow lines
  rotateToPath: boolean // spiral, concentric, path: rotate shapes to the local tangent
  jitter: number // seeded random placement offset, mm (0 = off)
  paths: PathDef[] // path mode geometry
}

// 'uniform' behaves like dropTouching measured against the boundary inset by
// clipMargin: holes stay whole and the band along the edge is at least that
// wide (the default). 'clip' cuts holes at the inset instead.
export type ClipMode = 'uniform' | 'clip' | 'dropOutside' | 'dropTouching'

// Default uniform edge band: 2% of the smaller canvas side.
export function defaultEdgeBand(width: number, height: number): number {
  return Math.round(0.02 * Math.min(width, height) * 10) / 10
}

export interface ExportSettings {
  thickness: number // mm
  tolerance: number // chord tolerance in mm for tessellating curves
  dxfBoundary: boolean // include the boundary outline on its own DXF layer
  splitCols: number // STEP split grid (1 x 1 = no split)
  splitRows: number
}

// Version history: 1 = v1 (point/line controllers only). 2 = v2-M2 adds
// curve/polyline controllers and the singleSide/infinite/syncGroup options.
// Loading migrates forward; older files always restore (see coerceDocument).
export interface DocumentModel {
  version: 2
  canvas: { width: number; height: number } // mm
  boundary: Boundary
  shape: ShapeStyle
  // Morph target: the Shape channel field (0..1) interpolates each hole's
  // outline from `shape` toward `shapeB`.
  shapeB: ShapeStyle
  customShape: CustomShape
  layout: LayoutConfig
  channels: Record<ChannelId, ControllerSet>
  background?: BackgroundImage | null
  // Measurement guides pulled from the edge rulers. axis 'x' is a vertical
  // line at x = value (mm); purely a design aid, never part of generation.
  guides: GuideLine[]
  clip: ClipMode
  clipMargin: number // minimum clearance from the boundary edge, mm
  export: ExportSettings
}

export interface GuideLine {
  id: string
  axis: 'x' | 'y'
  value: number
}

// Geometry interchange types. A ring is a list of [x, y] pairs without a
// duplicated closing vertex. A poly is [outer, ...holes]. Winding is
// normalized where it matters: outer CCW, holes CW.
export type Pt = [number, number]
export type Ring = Pt[]
export type Poly = Ring[]
export type MultiPoly = Poly[]

export interface CircleInfo {
  cx: number
  cy: number
  r: number
}

// One cut in the panel. `circle` is set when the outline is an untouched
// analytic circle (kept exact in STEP export).
export interface HolePoly {
  poly: Poly
  circle?: CircleInfo
}

export interface GenStats {
  instances: number
  holes: number
  ms: number
  capped: boolean
}

export interface GenResult {
  boundary: Ring
  boundaryCircle: CircleInfo | null
  holes: HolePoly[]
  stats: GenStats
}

export const MIN_SPACING = 0.4
export const MAX_INSTANCES = 30000

export function defaultDocument(): DocumentModel {
  return {
    version: 2,
    canvas: { width: 120, height: 80 },
    boundary: { kind: 'rect', width: 120, height: 80, cornerRadius: 0, radius: 50, nodes: [] },
    shape: { kind: 'circle', slotRatio: 0.4, sides: 6, starPoints: 5, starInner: 0.5 },
    shapeB: { kind: 'polygon', slotRatio: 0.4, sides: 6, starPoints: 5, starInner: 0.5 },
    customShape: { layers: [] },
    layout: {
      mode: 'grid',
      seed: 1,
      grid: { pattern: 'hex', crossRowSpin: 0, spinBy: 0, angle: 0 },
      crosshatch: { angleA: 45, angleB: -45 },
      voronoi: { cornerRadius: 0 },
      flow: { cap: 'round' },
      rotateToPath: true,
      jitter: 0,
      paths: [],
    },
    channels: {
      size: { base: 3, controllers: [], image: null },
      spacing: { base: 7, controllers: [], image: null },
      angle: { base: 0, controllers: [], image: null },
      shape: { base: 0, controllers: [], image: null },
    },
    background: null,
    guides: [],
    clip: 'uniform',
    clipMargin: defaultEdgeBand(120, 80), // 1.6 mm on the default canvas
    export: { thickness: 3, tolerance: 0.05, dxfBoundary: true, splitCols: 2, splitRows: 2 },
  }
}
